import { useEffect, useState } from "react";
import { Loader2, X, PenSquare, AlertTriangle, Send } from "lucide-react";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";

/** Editable single-field row. Declared at module scope so React keeps the
 *  input mounted between parent re-renders (no unstable-nested-component
 *  warning + no focus loss between keystrokes). */
function EditField({ label, name, value, onChange, type = "text", mono = false, maxLength, className = "" }) {
  return (
    <div className={className}>
      <label className="block text-[10px] font-bold uppercase tracking-[0.16em] text-gray-600 mb-1">{label}</label>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(name, e.target.value)}
        className={`ps-input w-full ${mono ? "font-mono" : ""}`}
        maxLength={maxLength}
        data-testid={`edit-gate-${name}`}
      />
    </div>
  );
}

/**
 * EditGateEventDialog — operator-accessible dialog for correcting the
 * whitelist of editable fields on a live gate event (booking, ISO type,
 * seal, contact, driver info, vessel, etc.). Explicitly does NOT show
 * container_number / gate_type / yard / carrier — those are identity fields
 * corrected via the "Correct carrier" flow or delete+re-create.
 *
 * If the event has status=EDI_SENT, saving flips ``edi_resend_pending: true``
 * on the row and the dialog surfaces a "Re-send EDI now" action button so
 * the operator can push a fresh CODECO with the corrected payload.
 *
 * Backend: PATCH /api/gate-events/{id} + POST /api/gate-events/{id}/resend-edi
 */
export default function EditGateEventDialog({ event, onClose, onDone }) {
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);
  const [resending, setResending] = useState(false);
  const [resendPending, setResendPending] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (!event) { setForm(null); return; }
    // datetime-local wants "YYYY-MM-DDTHH:mm" — trim seconds/timezone off ISO.
    const et = event.event_time ? new Date(event.event_time) : null;
    const pad = (n) => String(n).padStart(2, "0");
    const dtInput = et
      ? `${et.getFullYear()}-${pad(et.getMonth() + 1)}-${pad(et.getDate())}T${pad(et.getHours())}:${pad(et.getMinutes())}`
      : "";
    setForm({
      container_iso_type: event.container_iso_type || event.iso_type || "",
      event_time: dtInput,
      booking_number: event.booking_number || "",
      bill_of_lading: event.bill_of_lading || "",
      transport_ref: event.transport_ref || "",
      seal_number: event.seal_number || "",
      vessel_name: event.vessel_name || "",
      contact_number: event.contact_number || "",
      customer: event.customer || "",
      truck_plate: event.truck_plate || "",
      driver_name: event.driver_name || "",
      driver_license: event.driver_license || "",
      chassis_number: event.chassis_number || "",
      grade: event.grade || "",
      condition: event.condition || "",
      notes: event.notes || "",
    });
    setResendPending(!!event.edi_resend_pending);
    setSaved(false);
  }, [event]);

  if (!event || !form) return null;
  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const wasEdiSent = event.status === "EDI_SENT";

  const save = async () => {
    setBusy(true);
    try {
      // Only send fields that actually changed vs the original event — the
      // backend already handles partial updates but we keep the audit-log
      // diff clean by not sending no-ops.
      const original = {
        container_iso_type: event.container_iso_type || event.iso_type || "",
        event_time: form.event_time,   // pre-computed input value, ok to compare literally
        booking_number: event.booking_number || "",
        bill_of_lading: event.bill_of_lading || "",
        transport_ref: event.transport_ref || "",
        seal_number: event.seal_number || "",
        vessel_name: event.vessel_name || "",
        contact_number: event.contact_number || "",
        customer: event.customer || "",
        truck_plate: event.truck_plate || "",
        driver_name: event.driver_name || "",
        driver_license: event.driver_license || "",
        chassis_number: event.chassis_number || "",
        grade: event.grade || "",
        condition: event.condition || "",
        notes: event.notes || "",
      };
      const changed = {};
      Object.keys(form).forEach((k) => {
        const cur = (form[k] ?? "").toString().trim();
        const orig = (original[k] ?? "").toString().trim();
        if (k === "event_time") {
          // Compare literal input strings — user only touches this if intentional.
          if (form.event_time && form.event_time !== (event.event_time || "").slice(0, 16)) {
            changed.event_time = new Date(form.event_time).toISOString();
          }
        } else if (cur !== orig) {
          changed[k] = cur || null;
        }
      });
      if (Object.keys(changed).length === 0) {
        toast.info("No changes to save");
        setBusy(false);
        return;
      }
      const { data } = await api.patch(`/gate-events/${event.id}`, changed);
      toast.success(`Event updated · ${Object.keys(changed).length} field(s)`);
      setResendPending(!!data.edi_resend_pending);
      setSaved(true);
      // If the event was NOT EDI_SENT, close immediately. If it was, keep
      // the dialog open so the operator can hit "Re-send EDI".
      if (!wasEdiSent) {
        onDone?.(data);
      }
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const resendEdi = async () => {
    setResending(true);
    try {
      const { data } = await api.post(`/gate-events/${event.id}/resend-edi`);
      if (data.status === "SENT") {
        toast.success(`EDI re-sent to ${data.carrier}`);
      } else if (data.status === "FAILED") {
        toast.error(`EDI re-send failed: ${data.last_error?.slice(0, 100) || "unknown"}`);
      } else {
        toast.info(`EDI re-send status: ${data.status}`);
      }
      setResendPending(false);
      onDone?.(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setResending(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white border border-gray-200 max-w-3xl w-full max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="edit-gate-dialog">
        <div className="px-5 py-3 border-b border-gray-200 flex items-center justify-between sticky top-0 bg-white z-10">
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-gray-500">Edit gate event</div>
            <div className="text-sm font-bold font-mono mt-0.5">
              {event.container_number} · {event.gate_type} · {(event.last_carrier || event.carrier_code || "—")}
            </div>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-700"><X className="w-5 h-5" /></button>
        </div>

        {wasEdiSent && (
          <div className="mx-5 mt-4 flex items-start gap-2 p-3 border border-amber-200 bg-amber-50 text-amber-900 text-xs rounded-sm">
            <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
            <span>
              This event has already been sent to the carrier as EDI. Saving will flag it for <b>EDI re-send</b> so the carrier&apos;s copy is corrected.               Use the &quot;Re-send EDI now&quot; button after saving.
            </span>
          </div>
        )}

        {resendPending && saved && (
          <div className="mx-5 mt-4 flex items-center justify-between gap-3 p-3 border border-blue-200 bg-blue-50 text-blue-900 text-xs rounded-sm">
            <div className="flex items-start gap-2">
              <Send className="w-4 h-4 mt-0.5 shrink-0" />
              <span>Event updated. EDI re-send is pending — the carrier still has the old data.</span>
            </div>
            <button className="ps-btn ps-btn-primary h-7 text-xs" onClick={resendEdi} disabled={resending} data-testid="edit-gate-resend-edi">
              {resending ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Sending…</> : <><Send className="w-3.5 h-3.5" /> Re-send EDI now</>}
            </button>
          </div>
        )}

        <div className="p-5 space-y-4">
          <section>
            <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-gray-500 mb-3">Timing &amp; Unit</div>
            <div className="grid grid-cols-2 gap-3">
              <EditField label="Event time" name="event_time" value={form.event_time} onChange={update} type="datetime-local" />
              <EditField label="ISO type / unit size" name="container_iso_type" value={form.container_iso_type} onChange={update} mono maxLength={8} />
              <EditField label="Grade" name="grade" value={form.grade} onChange={update} maxLength={1} />
              <EditField label="Condition" name="condition" value={form.condition} onChange={update} />
            </div>
          </section>

          <section>
            <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-gray-500 mb-3">References</div>
            <div className="grid grid-cols-2 gap-3">
              <EditField label="Booking / Service-order #" name="booking_number" value={form.booking_number} onChange={update} mono />
              <EditField label="Bill of lading" name="bill_of_lading" value={form.bill_of_lading} onChange={update} mono />
              <EditField label="Transport reference (RFF+TN)" name="transport_ref" value={form.transport_ref} onChange={update} mono />
              <EditField label="Seal number" name="seal_number" value={form.seal_number} onChange={update} mono />
              <EditField label="Vessel" name="vessel_name" value={form.vessel_name} onChange={update} className="col-span-2" maxLength={80} />
            </div>
          </section>

          <section>
            <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-gray-500 mb-3">Parties &amp; Transport</div>
            <div className="grid grid-cols-2 gap-3">
              <EditField label="Consignee / Customer" name="customer" value={form.customer} onChange={update} className="col-span-2" />
              <EditField label="Contact number" name="contact_number" value={form.contact_number} onChange={update} mono />
              <EditField label="Truck plate" name="truck_plate" value={form.truck_plate} onChange={update} mono />
              <EditField label="Driver name" name="driver_name" value={form.driver_name} onChange={update} />
              <EditField label="Driver license" name="driver_license" value={form.driver_license} onChange={update} mono maxLength={40} />
              <EditField label="Chassis number" name="chassis_number" value={form.chassis_number} onChange={update} mono className="col-span-2" />
            </div>
          </section>

          <section>
            <label className="block text-[10px] font-bold uppercase tracking-[0.16em] text-gray-600 mb-1">Notes</label>
            <textarea value={form.notes} onChange={(e) => update("notes", e.target.value)} rows={2} className="ps-input w-full" data-testid="edit-gate-notes" />
          </section>
        </div>

        <div className="px-5 py-3 border-t border-gray-200 flex justify-end gap-2 bg-gray-50 sticky bottom-0">
          <button className="ps-btn ps-btn-secondary" onClick={onClose} disabled={busy}>Close</button>
          <button className="ps-btn ps-btn-primary" onClick={save} disabled={busy} data-testid="edit-gate-save">
            {busy ? <><Loader2 className="w-4 h-4 animate-spin" /> Saving…</> : <><PenSquare className="w-4 h-4" /> Save changes</>}
          </button>
        </div>
      </div>
    </div>
  );
}
